import React, { useState, useEffect } from 'react';
import { ColeccionManga, TomoManga } from '../types';
import { useAppState } from '../context/AppStateContext';
import { useDialog } from '../hooks/useDialog';
import Dialog from './Dialog';
import { CheckCircle, X, Star, Clock, AlertCircle, BookOpen } from 'lucide-react';

interface TomoPuntosModalProps {
  coleccion: ColeccionManga;
  onClose: () => void;
}

const TomoPuntosModal: React.FC<TomoPuntosModalProps> = ({ coleccion, onClose }) => {
  const { state, dispatch } = useAppState();
  const { dialog, showConfirm, hideDialog } = useDialog();
  const [selectedAction, setSelectedAction] = useState<'marcar' | 'quitar' | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Obtener la colección actualizada del estado global
  const coleccionActualizada = state.coleccionesManga.find(c => c.id === coleccion.id) || coleccion;
  
  const tomosLeidos = coleccionActualizada.tomos.filter(t => t.estado === 'leido');
  const tomosConPuntos = tomosLeidos.filter(t => t.fechaLecturaReciente);
  const tomosSinPuntos = tomosLeidos.filter(t => !t.fechaLecturaReciente);

  // Limpiar acción seleccionada cuando cambian los datos
  useEffect(() => {
    if (selectedAction && tomosSinPuntos.length === 0 && selectedAction === 'marcar') {
      setSelectedAction(null);
    }
    if (selectedAction && tomosConPuntos.length === 0 && selectedAction === 'quitar') {
      setSelectedAction(null);
    }
  }, [tomosSinPuntos.length, tomosConPuntos.length, selectedAction]);

  const handleMarcarTodos = async () => {
    showConfirm(
      'Marcar Tomos como Recientes',
      `¿Estás seguro de que quieres marcar ${tomosSinPuntos.length} tomo(s) como leído(s) recientemente para que cuenten para puntos?`,
      async () => {
        setIsProcessing(true);
        try {
          tomosSinPuntos.forEach(tomo => {
            dispatch({ 
              type: 'UPDATE_TOMO_MANGA', 
              payload: { 
                coleccionId: coleccion.id, 
                tomoId: tomo.id, 
                updates: { 
                  fechaLecturaReciente: Date.now()
                } 
              } 
            });
          });
          setSelectedAction(null);
        } finally {
          setIsProcessing(false);
        }
      },
      undefined,
      'Marcar Todos',
      'Cancelar'
    );
  };

  const handleQuitarTodos = async () => {
    showConfirm(
      'Quitar Puntos de Tomos',
      `¿Estás seguro de que quieres quitar los puntos de ${tomosConPuntos.length} tomo(s)? Ya no contarán para puntos.`,
      async () => {
        setIsProcessing(true);
        try {
          tomosConPuntos.forEach(tomo => {
            dispatch({ 
              type: 'UPDATE_TOMO_MANGA', 
              payload: { 
                coleccionId: coleccion.id, 
                tomoId: tomo.id, 
                updates: { 
                  fechaLecturaReciente: undefined
                } 
              } 
            });
          });
          setSelectedAction(null);
        } finally {
          setIsProcessing(false);
        }
      },
      undefined,
      'Quitar Todos',
      'Cancelar'
    );
  };

  const handleToggleTomo = (tomo: TomoManga) => {
    if (tomo.fechaLecturaReciente) {
      // Quitar puntos
      showConfirm(
        'Quitar Puntos del Tomo',
        `¿Estás seguro de que quieres quitar los puntos del tomo #${tomo.numero}? Ya no contará para puntos.`,
        () => {
          dispatch({ 
            type: 'UPDATE_TOMO_MANGA', 
            payload: { 
              coleccionId: coleccion.id, 
              tomoId: tomo.id, 
              updates: { 
                fechaLecturaReciente: undefined
              } 
            } 
          });
        },
        undefined,
        'Quitar Puntos',
        'Cancelar'
      );
    } else {
      // Agregar puntos
      showConfirm(
        'Agregar Puntos al Tomo',
        `¿Estás seguro de que quieres marcar el tomo #${tomo.numero} como leído recientemente para que cuente para puntos?`,
        () => {
          dispatch({ 
            type: 'UPDATE_TOMO_MANGA', 
            payload: { 
              coleccionId: coleccion.id, 
              tomoId: tomo.id, 
              updates: { 
                fechaLecturaReciente: Date.now()
              } 
            } 
          });
        },
        undefined,
        'Agregar Puntos',
        'Cancelar'
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Gestión de Puntos - {coleccionActualizada.titulo}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Gestiona qué tomos cuentan para puntos. Los cambios se aplican inmediatamente.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">Total Leídos</span>
            </div>
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-100 mt-1">
              {tomosLeidos.length}
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              Tomos marcados como leídos
            </p>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Star className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-800 dark:text-green-200">Con Puntos</span>
            </div>
            <p className="text-2xl font-bold text-green-900 dark:text-green-100 mt-1">
              {tomosConPuntos.length}
            </p>
            <p className="text-xs text-green-700 dark:text-green-300 mt-1">
              Cuentan para recompensas
            </p>
          </div>
          
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Sin Puntos</span>
            </div>
            <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100 mt-1">
              {tomosSinPuntos.length}
            </p>
            <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
              Solo registro histórico
            </p>
          </div>
        </div>

        {/* Acciones Masivas */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
            Acciones Masivas
          </h3>
          <div className="flex space-x-3">
            {tomosSinPuntos.length > 0 && (
              <button
                onClick={() => setSelectedAction('marcar')}
                disabled={isProcessing}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white font-medium rounded-md transition-colors duration-200 flex items-center space-x-2 disabled:cursor-not-allowed"
              >
                <CheckCircle className="w-4 h-4" />
                <span>Marcar {tomosSinPuntos.length} como recientes</span>
              </button>
            )}
            
            {tomosConPuntos.length > 0 && (
              <button
                onClick={() => setSelectedAction('quitar')}
                disabled={isProcessing}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white font-medium rounded-md transition-colors duration-200 flex items-center space-x-2 disabled:cursor-not-allowed"
              >
                <X className="w-4 h-4" />
                <span>Quitar puntos de {tomosConPuntos.length}</span>
              </button>
            )}
            
            {tomosSinPuntos.length === 0 && tomosConPuntos.length === 0 && (
              <div className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-md">
                <span className="text-sm">No hay acciones disponibles</span>
              </div>
            )}
          </div>
        </div>

        {/* Lista de Tomos */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
            Tomos Leídos
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {tomosLeidos
              .sort((a, b) => a.numero - b.numero)
              .map(tomo => (
                <div
                  key={tomo.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      #{tomo.numero}
                    </span>
                    
                    {tomo.fechaLecturaReciente ? (
                      <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
                        <Star className="w-4 h-4" />
                        <span className="text-xs">
                          Con puntos ({new Date(tomo.fechaLecturaReciente).toLocaleDateString()})
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 text-yellow-600 dark:text-yellow-400">
                        <Clock className="w-4 h-4" />
                        <span className="text-xs">
                          Sin puntos
                          {tomo.fechaLectura && ` (leído ${new Date(tomo.fechaLectura).toLocaleDateString()})`}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => handleToggleTomo(tomo)}
                    disabled={isProcessing}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors duration-200 ${
                      tomo.fechaLecturaReciente
                        ? 'bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-300'
                        : 'bg-green-100 hover:bg-green-200 text-green-700 dark:bg-green-900/30 dark:hover:bg-green-900/50 dark:text-green-300'
                    } disabled:cursor-not-allowed`}
                  >
                    {tomo.fechaLecturaReciente ? 'Quitar Puntos' : 'Agregar Puntos'}
                  </button>
                </div>
              ))}
          </div>
          
          {tomosLeidos.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              No hay tomos leídos en esta colección
            </p>
          )}
        </div>

        {/* Confirmación de Acción Masiva */}
        {selectedAction && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                  Confirmar Acción
                </h4>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-3">
                  {selectedAction === 'marcar' 
                    ? `¿Estás seguro de que quieres marcar ${tomosSinPuntos.length} tomo(s) como leído(s) recientemente para que cuenten para puntos?`
                    : `¿Estás seguro de que quieres quitar los puntos de ${tomosConPuntos.length} tomo(s)?`
                  }
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={selectedAction === 'marcar' ? handleMarcarTodos : handleQuitarTodos}
                    disabled={isProcessing}
                    className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white font-medium rounded-md transition-colors duration-200 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? 'Procesando...' : 'Confirmar'}
                  </button>
                  <button
                    onClick={() => setSelectedAction(null)}
                    disabled={isProcessing}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-md transition-colors duration-200 disabled:cursor-not-allowed"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-md transition-colors duration-200"
          >
            Cerrar
          </button>
        </div>
      </div>
      
      {/* Dialog de confirmación */}
      {dialog && (
        <Dialog
          isOpen={dialog.isOpen}
          onClose={hideDialog}
          title={dialog.title}
          message={dialog.message}
          onConfirm={dialog.onConfirm}
          onCancel={dialog.onCancel}
          confirmText={dialog.confirmText}
          cancelText={dialog.cancelText}
          type={dialog.type}
        />
      )}
    </div>
  );
};

export default TomoPuntosModal;